import { useRef, useEffect, type KeyboardEvent, type ClipboardEvent } from 'react';
import { useMfa } from '@/hooks/useMfa';
import { useLogin } from '@/hooks/useLogin';
import { useLocalization } from '@/hooks/useLocalization';
import { useSignup } from './hooks/useSignup';
import { useOnboardingAnalytics } from './hooks/useOnboardingAnalytics';
import { AuthLayout } from './components/AuthLayout';
import { AuthLogo } from './components/AuthLogo';
import { SignInCard } from './components/SignInCard';
import { CreateAccountCard } from './components/CreateAccountCard';
import styles from './MfaPage.module.css';

/**
 * MfaPage — email-based Multi-Factor Authentication page.
 *
 * Mirrors Flutter WebMfaVerificationPage + WebMfaForms + WebEmailBasedMfaOtpWidget, with the
 * VerifyOtpScreenConstants / WebMfaPageConstants texts:
 *   1. Auto-send email OTP on mount (after 1s delay); a spinner until it is sent
 *   2. 6-digit OTP input, expiry countdown, resend cooldown
 *   3. Verify OTP → JWT validation → "User authenticated successfully" dialog → destination
 *   4. Too many OTP requests → "Error" dialog → logout → /login
 *
 * Shown as a modal dialog over the /login view (Sign in + Create account cards), which stays
 * visible but inert behind it. It can't be dismissed — the user verifies or logs out.
 * No MFA-specific reference UI exists.
 *
 * Flutter source files:
 *   - presentation/web/auth/web_mfa_verification/web_mfa_verification_page.dart
 *   - presentation/web/auth/web_mfa_verification/web_mfa_forms.dart
 *   - presentation/web/auth/web_mfa_verification/widgets/web_email_based_mfa_otp_widget.dart
 */

// React 18 has no `inert` prop yet — pass the attribute through as-is
const INERT = { inert: '' } as object;

/** The /login view's cards, shown (inert) behind the MFA dialog. */
function AuthCardsBackdrop() {
  const login = useLogin();
  const signup = useSignup();
  return (
    <>
      <SignInCard login={login} onForgotPassword={() => {}} />
      <CreateAccountCard signup={signup} />
    </>
  );
}

function formatTime(totalSeconds: number): string {
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

export function MfaPage() {
  const { t } = useLocalization();
  const mfa = useMfa();
  useOnboardingAnalytics();

  // OTP input refs for individual digit boxes
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Modal — keep the page behind it from scrolling (same as MobileNavMenu)
  useEffect(() => {
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
  }, []);

  // Focus first input when OTP step is shown
  useEffect(() => {
    if (mfa.step === 'otp' && mfa.otpSent) {
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [mfa.step, mfa.otpSent]);

  // ── OTP Input Handlers ─────────────────────────────────────────

  function handleDigitChange(index: number, value: string) {
    if (!/^\d?$/.test(value)) return;

    const currentOtp = mfa.otp.split('');
    while (currentOtp.length < mfa.otpLength) currentOtp.push('');
    currentOtp[index] = value;
    mfa.setOtp(currentOtp.join(''));

    if (value && index < mfa.otpLength - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }

  function handleKeyDown(index: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !mfa.otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter' && mfa.canVerify) {
      mfa.handleVerifyOtp();
    }
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, mfa.otpLength);
    if (pasted.length > 0) {
      mfa.setOtp(pasted);
      const focusIndex = Math.min(pasted.length, mfa.otpLength - 1);
      inputRefs.current[focusIndex]?.focus();
    }
  }

  // ── Render ─────────────────────────────────────────────────────

  return (
    <>
      <div {...INERT}>
        <AuthLayout>
          <AuthCardsBackdrop />
        </AuthLayout>
      </div>

      <div className={styles.overlay}>
        <div
          className={styles.dialog}
          role="dialog"
          aria-modal="true"
          aria-labelledby={mfa.step === 'otp' ? 'mfa-title' : undefined}
          aria-busy={mfa.step === 'loading' || undefined}
        >
          {/* ── Sending the first OTP — Flutter shows only a progress indicator ── */}
          {mfa.step === 'loading' && (
            <div className={styles.loading}>
              <div className={styles.spinner} />
            </div>
          )}

          {/* ── OTP Input Step (WebEmailBasedMfaOtpWidget) ── */}
          {mfa.step === 'otp' && (
            <>
              <AuthLogo />

              <div className={styles.iconWrap}>
                <div className={styles.icon} aria-hidden>✉️</div>
              </div>

              <h1 id="mfa-title" className={styles.title}>{t('Verify Email OTP')}</h1>

              <p className={styles.instruction}>
                {`${t('An OTP has been sent to your registered email address')} `}
                <span className={styles.maskedEmail}>{`${mfa.maskedEmail}.`}</span>
                {` ${t('Please enter the OTP to proceed.')}`}
              </p>

              {mfa.error && (
                <div className={styles.error} role="alert">{t(mfa.error)}</div>
              )}

              <div className={styles.otpRow}>
                {Array.from({ length: mfa.otpLength }).map((_, i) => (
                  <input
                    key={i}
                    ref={(el) => { inputRefs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={mfa.otp[i] ?? ''}
                    onChange={(e) => handleDigitChange(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    onPaste={i === 0 ? handlePaste : undefined}
                    disabled={mfa.isLoading}
                    autoComplete="one-time-code"
                    aria-label={`${t('Enter OTP')} ${i + 1}`}
                    className={`${styles.otpInput} ${mfa.otp[i] ? styles.otpInputFilled : ''}`}
                  />
                ))}
              </div>

              {mfa.showOtpExpiry && (
                <div className={`${styles.expiry} ${mfa.otpExpiresIn <= 0 ? styles.expired : ''}`}>
                  {mfa.otpExpiresIn <= 0
                    ? t('OTP expired, Please Resend again.')
                    : `${t('OTP expires in')} ${formatTime(mfa.otpExpiresIn)} ${t('mins')}`}
                </div>
              )}

              <div className={styles.resendRow}>
                <div className={styles.resendText}>
                  {t("Didn't receive code?")}{' '}
                  <button
                    type="button"
                    className={styles.resendBtn}
                    onClick={mfa.canResend ? mfa.handleSendOtp : undefined}
                    disabled={!mfa.canResend}
                  >
                    {t(' Resend OTP').trim()}
                  </button>
                </div>
                {mfa.resendCooldown > 0 && (
                  <span className={styles.cooldown}>{formatTime(mfa.resendCooldown)}</span>
                )}
              </div>

              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.logoutBtn}
                  onClick={mfa.handleLogout}
                  disabled={mfa.isLoading}
                >
                  {t('Logout')}
                </button>
                <button
                  type="button"
                  className={styles.verifyBtn}
                  onClick={mfa.handleVerifyOtp}
                  disabled={!mfa.canVerify}
                >
                  {t('Verify')}
                </button>
              </div>
            </>
          )}

          {/* ── Success — Flutter CustomDialog.showWarningDialog(Constants.authenticatedSuccessMsg) ── */}
          {mfa.step === 'success' && (
            <div className={styles.message} role="alertdialog" aria-live="polite">
              <p className={styles.messageText}>{t('User authenticated successfully')}</p>
              <button
                type="button"
                className={styles.messageBtn}
                onClick={mfa.handleAuthenticatedContinue}
                autoFocus
              >
                {t('Continue')}
              </button>
            </div>
          )}

          {/* Loading overlay while sending / verifying */}
          {mfa.isLoading && mfa.step !== 'loading' && (
            <div className={styles.busyOverlay}>
              <div className={styles.spinner} />
            </div>
          )}
        </div>
      </div>

      {/* 429 on send — Flutter CustomDialog.showWarningDialog(title: 'Error', ErrorConstants.tooManyRequestsError) */}
      {mfa.showTooManyRequests && (
        <div className={styles.alertOverlay}>
          <div className={styles.alert} role="alertdialog" aria-modal="true" aria-labelledby="mfa-error-title">
            <h2 id="mfa-error-title" className={styles.alertTitle}>{t('Error')}</h2>
            <p className={styles.messageText}>{t('Too many requests. Please try again after some time.')}</p>
            <button
              type="button"
              className={styles.messageBtn}
              onClick={mfa.handleTooManyRequestsOk}
              autoFocus
            >
              {t('OK')}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
